import React, { useEffect, useState } from 'react'
import {
  Drawer, Form, Input, InputNumber, Select, Switch, Button,
  Checkbox, Divider, Space, Alert, Typography, Tag, Tooltip,
} from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, CopyOutlined } from '@ant-design/icons'
import type { ProviderInfo, ConfigSchemaField, ConnectorConfig, TestResult } from '../types'
import { useTestConnector, useSaveConnectorConfig } from '@/shared/api/connectorApi'
import { App } from 'antd'

const { Text } = Typography
const { TextArea } = Input

interface Props {
  open: boolean
  onClose: () => void
  providers: ProviderInfo[]
  companyId: number
  editingConfig?: ConnectorConfig | null
}

const ConnectorConfigDrawer: React.FC<Props> = ({
  open, onClose, providers, companyId, editingConfig,
}) => {
  const [form] = Form.useForm()
  const { message } = App.useApp()
  const [selectedProvider, setSelectedProvider] = useState<string>('')
  const [testResult, setTestResult] = useState<TestResult | null>(null)

  const testMutation = useTestConnector()
  const saveMutation = useSaveConnectorConfig()

  // 监听表单值变化，确保 depends_on 条件字段能响应式显示/隐藏
  const watchedValues = Form.useWatch([], form)

  // 初始化表单
  useEffect(() => {
    if (!open) {
      setTestResult(null)
      return
    }
    if (editingConfig) {
      setSelectedProvider(editingConfig.provider)
      // JSON 字段需要序列化为字符串显示
      const values: Record<string, any> = { ...editingConfig, enabled_domains: editingConfig.enabled_domains || [] }
      const provider = providers.find(p => p.name === editingConfig.provider)
      if (provider) {
        for (const field of provider.config_schema) {
          if (field.type === 'json' && values[field.key] && typeof values[field.key] === 'object') {
            values[field.key] = JSON.stringify(values[field.key], null, 2)
          }
        }
      }
      form.setFieldsValue(values)
    } else {
      form.resetFields()
      setSelectedProvider('')
      setTestResult(null)
    }
  }, [open, editingConfig, form, providers])

  // 获取当前 provider 的 schema
  const currentProvider = providers.find(p => p.name === selectedProvider)
  const schema = currentProvider?.config_schema || []

  const handleProviderChange = (value: string) => {
    setSelectedProvider(value)
    setTestResult(null)
    const provider = providers.find(p => p.name === value)
    if (provider) {
      const defaults: Record<string, any> = { provider: value, enabled_domains: provider.supported_domains }
      for (const field of provider.config_schema) {
        if (field.default !== undefined) {
          if (field.type === 'json') {
            defaults[field.key] = typeof field.default === 'object'
              ? JSON.stringify(field.default, null, 2)
              : field.default
          } else {
            defaults[field.key] = field.default
          }
        }
      }
      form.setFieldsValue(defaults)
    }
  }

  // 提交前将 JSON 字符串字段解析为对象
  const getFormValues = async () => {
    const values = await form.validateFields()
    for (const field of schema) {
      if (field.type === 'json' && values[field.key]) {
        if (typeof values[field.key] === 'string') {
          try {
            values[field.key] = JSON.parse(values[field.key])
          } catch {
            throw new Error(`${field.label} contains invalid JSON`)
          }
        }
      }
    }
    return values
  }

  const handleTest = async () => {
    try {
      const values = await getFormValues()
      const result = await testMutation.mutateAsync({ companyId, config: values })
      setTestResult(result)
      if (result.healthy) {
        message.success('Connection test passed')
      } else {
        message.error(result.error || 'Connection test failed')
      }
    } catch (e: any) {
      message.error(e.message || 'Validation failed')
    }
  }

  const handleSave = async () => {
    try {
      const values = await getFormValues()
      await saveMutation.mutateAsync({ companyId, config: values })
      message.success('Configuration saved')
      onClose()
    } catch (e: any) {
      message.error(e.message || 'Save failed')
    }
  }

  // 根据 depends_on 决定字段是否显示（watchedValues 变化时自动重渲染）
  const shouldShowField = (field: ConfigSchemaField): boolean => {
    if (!field.depends_on) return true
    return Object.entries(field.depends_on).every(
      ([key, value]) => watchedValues?.[key] === value
    )
  }

  // 加载模板到 JSON 字段
  const loadTemplate = (field: ConfigSchemaField) => {
    if (field.template) {
      form.setFieldsValue({
        [field.key]: JSON.stringify(field.template, null, 2)
      })
    }
  }

  // 渲染单个表单字段
  const renderField = (field: ConfigSchemaField) => {
    if (!shouldShowField(field)) return null

    const rules = field.required ? [{ required: true, message: `${field.label} is required` }] : []

    switch (field.type) {
      case 'password': {
        const hasValue = editingConfig?.[`has_${field.key}` as keyof typeof editingConfig]
        return (
          <Form.Item key={field.key} name={field.key} label={field.label} rules={rules}>
            <Input.Password
              placeholder={hasValue ? '(configured - leave empty to keep)' : field.placeholder}
            />
          </Form.Item>
        )
      }
      case 'select':
        return (
          <Form.Item key={field.key} name={field.key} label={field.label} rules={rules}>
            <Select>
              {(field.options || []).map(opt => (
                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        )
      case 'number':
        return (
          <Form.Item key={field.key} name={field.key} label={field.label} rules={rules}>
            <InputNumber min={field.min} max={field.max} style={{ width: '100%' }} />
          </Form.Item>
        )
      case 'boolean':
        return (
          <Form.Item key={field.key} name={field.key} label={field.label} valuePropName="checked">
            <Switch />
          </Form.Item>
        )
      case 'json':
        return (
          <Form.Item
            key={field.key}
            name={field.key}
            label={
              <Space>
                {field.label}
                {field.template && (
                  <Tooltip title="Load template">
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => loadTemplate(field)}
                    >
                      Template
                    </Button>
                  </Tooltip>
                )}
              </Space>
            }
            rules={[
              ...rules,
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '' || value.trim() === '{}') return Promise.resolve()
                  try {
                    JSON.parse(value)
                    return Promise.resolve()
                  } catch {
                    return Promise.reject(new Error('Invalid JSON format'))
                  }
                }
              }
            ]}
            extra={field.description}
          >
            <TextArea
              rows={10}
              placeholder='{"operation_name": {"method": "GET", "endpoint": "..."}}'
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
        )
      default:
        return (
          <Form.Item key={field.key} name={field.key} label={field.label} rules={rules}>
            <Input placeholder={field.placeholder} />
          </Form.Item>
        )
    }
  }

  // 将字段分为连接配置、高级配置、自定义操作
  const connectionFields = schema.filter(f =>
    ['base_url', 'auth_type', 'admin_username', 'admin_password', 'integration_token'].includes(f.key)
  )
  const customOpFields = schema.filter(f => f.type === 'json')
  const advancedFields = schema.filter(f =>
    !['base_url', 'auth_type', 'admin_username', 'admin_password', 'integration_token'].includes(f.key)
    && f.type !== 'json'
  )

  return (
    <Drawer
      title={editingConfig ? `Edit ${editingConfig.provider}` : 'Add Connector'}
      open={open}
      onClose={onClose}
      width={600}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={handleTest} loading={testMutation.isPending}>
            Test Connection
          </Button>
          <Button type="primary" onClick={handleSave} loading={saveMutation.isPending}>
            Save
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onValuesChange={() => setTestResult(null)}>
        {/* Provider 选择 */}
        <Form.Item name="provider" label="Provider" rules={[{ required: true }]}>
          <Select
            placeholder="Select a provider"
            onChange={handleProviderChange}
            disabled={!!editingConfig}
          >
            {providers.map(p => (
              <Select.Option key={p.name} value={p.name}>{p.display_name}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        {currentProvider && (
          <>
            {/* Enabled Domains */}
            <Form.Item
              name="enabled_domains"
              label="Enabled Domains"
              rules={[{ required: true, message: 'Select at least one domain' }]}
            >
              <Checkbox.Group>
                {currentProvider.supported_domains.map(d => (
                  <Checkbox key={d} value={d} style={{ marginBottom: 4 }}>
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>

            <Divider orientation="left" plain>Connection</Divider>
            {connectionFields.map(renderField)}

            <Divider orientation="left" plain>Advanced</Divider>
            {advancedFields.map(renderField)}

            {customOpFields.length > 0 && (
              <>
                <Divider orientation="left" plain>Custom Operations</Divider>
                {customOpFields.map(renderField)}
              </>
            )}
          </>
        )}
      </Form>

      {/* Test Result */}
      {testResult && (
        <div style={{ marginTop: 16 }}>
          <Divider orientation="left" plain>Test Result</Divider>
          <Alert
            type={testResult.healthy ? 'success' : 'error'}
            message={testResult.healthy ? 'Connection Healthy' : 'Connection Failed'}
            description={
              testResult.healthy ? (
                <div>
                  <Text type="secondary">Latency: {testResult.latency_ms}ms</Text>
                  {testResult.domain_status && (
                    <div style={{ marginTop: 8 }}>
                      {Object.entries(testResult.domain_status).map(([domain, info]) => (
                        <div key={domain} style={{ marginBottom: 4 }}>
                          {info.available ? (
                            <Tag icon={<CheckCircleOutlined />} color="success">{domain}</Tag>
                          ) : (
                            <Tag icon={<CloseCircleOutlined />} color="error">
                              {domain}: {info.reason}
                            </Tag>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Text type="danger">{testResult.error}</Text>
              )
            }
            showIcon
          />
        </div>
      )}
    </Drawer>
  )
}

export default ConnectorConfigDrawer
